import type {
  Lang,
  RoundState,
  ScoreBreakdown,
  SubmitInput,
  SubmitOutcome,
} from '@polyglot/engine';
import { finalizeIfExpired } from '@polyglot/engine';
import { api, ApiError, newEventId, serverNow } from '../lib/api';
import type { RoundDriver } from './driver';

export interface MatchView {
  id: string;
  kind: 'challenge' | 'live' | 'bot' | 'daily' | 'tournament';
  mode: 'classic' | 'polyglot' | 'duel' | 'sprint' | 'relax';
  status: string;
  rulesVersion: string;
  sharedLanguages: Lang[] | null;
  lexiconVersions: Partial<Record<Lang, string>>;
  createdAt: string;
  expiresAt: string | null;
  ranked: boolean;
  leaderboard: string;
  parentMatchId: string | null;
  serverNow: number;
  me: {
    seat: number;
    status: string;
    languages: Lang[] | null;
    startedAt: number | null;
    deadlineAt: number | null;
    state: RoundState | null;
    score: ScoreBreakdown | null;
  };
  rack: string[] | null;
  compatible: string[] | null;
  opponents: {
    seat: number;
    userId: string | null;
    displayName: string | null;
    isBot: boolean;
    status: string;
    words: number;
    languages: Lang[] | null;
    finalCents: number | null;
    state: RoundState | null;
    score: ScoreBreakdown | null;
  }[];
  result: { winners: number[]; tie: boolean } | null;
  /** Tournament round or daily challenge this match belongs to. */
  competition: { id: string; roundNo: number } | null;
  leg: number | null;
  legOrder: number | null;
  drawAudit: { seed: string; generatorVersion: string; rulesVersion: string } | null;
  live?: {
    startsAt: number | null;
    phase: string;
    opponentLocked: boolean;
    rematchId: string | null;
  } | null;
}

export async function fetchMatch(id: string): Promise<MatchView> {
  return api<MatchView>('GET', `/api/matches/${id}`);
}

/**
 * Online round driver: the server is the authority. Submissions carry a client event id and
 * are retried with the same id after a network error (idempotent on the server). The local
 * clock is aligned on the server clock for display only.
 */
export class OnlineDriver implements RoundDriver {
  readonly kind = 'online' as const;
  readonly lexicons = {};
  readonly hintsAllowed = false;
  private state: RoundState;
  private readonly listeners = new Set<() => void>();
  private readonly matchId: string;
  private timer: number;
  private refreshing = false;

  constructor(matchId: string, state: RoundState) {
    this.matchId = matchId;
    this.state = state;
    this.timer = window.setInterval(() => this.tick(), 250);
  }

  now(): number {
    return serverNow();
  }

  getState(): RoundState {
    return this.state;
  }

  subscribe(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private set(s: RoundState): void {
    this.state = s;
    for (const l of this.listeners) l();
    if (s.status === 'finished') this.dispose();
  }

  private tick(): void {
    // Display-side expiry; the official end comes from the server (sweeper) and is fetched.
    const local = finalizeIfExpired(this.state, this.now());
    if (local.status === 'finished' && !this.refreshing) {
      this.refreshing = true;
      void this.refresh().finally(() => {
        this.refreshing = false;
      });
    }
  }

  async refresh(): Promise<void> {
    try {
      const v = await fetchMatch(this.matchId);
      if (v.me.state) {
        if (v.me.status === 'finished' || v.me.state.words.length >= this.state.words.length)
          this.set(v.me.state);
      }
    } catch {
      /* retried on next tick */
    }
  }

  async submit(input: SubmitInput): Promise<SubmitOutcome> {
    const clientEventId = newEventId();
    let lastError: unknown = null;
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const r = await api<{ outcome: SubmitOutcome; state: RoundState | null }>(
          'POST',
          `/api/matches/${this.matchId}/submit`,
          {
            key: input.key,
            lang: input.lang ?? null,
            confirmFinal: input.confirmFinal ?? false,
            clientEventId,
          },
        );
        if (r.state) this.set(r.state);
        return r.outcome;
      } catch (e) {
        lastError = e;
        if (e instanceof ApiError && e.status === 429) return { ok: false, reason: 'rate_limited' };
        if (!(e instanceof ApiError) || e.status !== 0) throw e;
        await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
      }
    }
    throw lastError;
  }

  async stop(): Promise<void> {
    await api('POST', `/api/matches/${this.matchId}/stop`, {});
    await this.refresh();
  }

  dispose(): void {
    window.clearInterval(this.timer);
  }
}

/**
 * Live updates via Server-Sent Events (auto-reconnect with Last-Event-ID by the browser).
 * `onConnection` reports the stream state: 'reconnecting' after a loss, 'open' when back.
 */
export function watchMatch(
  matchId: string,
  onEvent: (type: string, data: Record<string, unknown>) => void,
  onConnection?: (state: 'open' | 'reconnecting') => void,
): () => void {
  const es = new EventSource(`/api/matches/${matchId}/events`);
  es.onopen = () => onConnection?.('open');
  es.onerror = () => {
    if (es.readyState !== EventSource.CLOSED) onConnection?.('reconnecting');
  };
  const types = [
    'round_started',
    'progress',
    'player_finished',
    'match_finished',
    'encounter_finished',
    'player_joined',
    'player_locked',
    'draw_ready',
    'countdown',
    'presence',
    'reaction',
    'rematch',
  ];
  for (const type of types) {
    es.addEventListener(type, (ev) => {
      try {
        onEvent(type, JSON.parse((ev as MessageEvent<string>).data) as Record<string, unknown>);
      } catch {
        onEvent(type, {});
      }
    });
  }
  return () => es.close();
}
